import { FC, ReactElement } from "react";
import { Swiper as SwiperCoreComponent, SwiperSlide } from "swiper/react";
import SwiperCore, { Navigation } from "swiper";
import Link from "next/link";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight } from "@fortawesome/pro-regular-svg-icons";
import Image from "next/image";
import { shimmer, toBase64 } from "src/js/constants";
import "swiper/swiper.min.css";
import "swiper/components/navigation/navigation.min.css";

SwiperCore.use([Navigation]);

export interface SwiperSlideType {
    link?: string;
    image?: string;
    showTitle: boolean;
    title?: string;
    externalLink?: boolean;
    backgroundColor?: string;
    squareSlide?: boolean;
    desktopMinSize?: number;
    slideWidth: number;
    slideHeight: number;
}

interface Props {
    uuid: string;
    slidesPerView: number;
    center?: boolean;
    slides: Array<SwiperSlideType>;
    preventClicks?: boolean;
    loop?: boolean;
    desktopMinSize: number;
    slideWidth: number;
    slideHeight: number;
    selectedCampus: string;
    sectionTitle?: string;
    pageName?: string;
}

const SwiperItem: FC<{
    slide: SwiperSlideType;
    desktopMinSize: number;
    slideHeight: number;
    slideWidth: number;
    selectedCampus: string;
    sectionTitle?: string;
    pageName?: string;
}> = ({
    slide,
    desktopMinSize,
    selectedCampus,
    sectionTitle,
    pageName,
}): ReactElement => {
    const {
        title,
        image,
        showTitle,
        link,
        externalLink,
        backgroundColor,
        slideWidth,
        slideHeight,
    } = slide;

    let imageContentToWrite: ReactElement | null = null;

    if (image) {
        let wrapperClassName =
            "photo-wrapper rel-pos background-white ec-border-radius full-width";

        if (slide.squareSlide) {
            wrapperClassName += " square";
        } else {
            wrapperClassName += " wide";
        }

        const actualImage = (
            <Image
                className="ec-border-radius animated"
                layout="fill"
                sizes="270px"
                // width={slideWidth}
                // height={slideHeight}
                objectFit={"cover"}
                objectPosition={"center"}
                src={image}
                alt={title}
                placeholder="blur"
                blurDataURL={`data:image/svg+xml;base64,${toBase64(
                    shimmer(250, 166)
                )}`}
            />
        );

        if (backgroundColor) {
            imageContentToWrite = (
                <div
                    className={wrapperClassName}
                    style={{ backgroundColor: backgroundColor }}
                >
                    {actualImage}
                </div>
            );
        } else {
            imageContentToWrite = (
                <div className={wrapperClassName}>{actualImage}</div>
            );
        }
    }

    const content = (
        <div>
            <div className="ec-light-box-shadow ec-border-radius">
                {imageContentToWrite}

                {showTitle ? (
                    <h4 className="m-y-spacer-none text-center m-t-spacer-sm ec-secondary-font text-dark">
                        {title}
                    </h4>
                ) : null}
            </div>
        </div>
    );

    if (link) {
        const linkClass = `d-block ec-border-radius slider ${
            process.browser && window.innerWidth > desktopMinSize
                ? "hover-grow"
                : ""
        }`;

        const finalSlideTitle = title ? title.replace(/ /g, "-") : "";
        const finalSectionTitle = sectionTitle
            ? sectionTitle.replace(/ /g, "-")
            : "";

        const id = `cc-egroups${pageName ? `-${pageName.toLowerCase()}` : ""}${
            selectedCampus ? `-${selectedCampus}` : ""
        }${
            finalSectionTitle ? `-find-${finalSectionTitle.toLowerCase()}` : ""
        }-${finalSlideTitle.toLowerCase()}`;
        return (
            <>
                {externalLink ? (
                    <a
                        href={link}
                        id={id}
                        className={linkClass}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {content}
                    </a>
                ) : (
                    <Link href={link}>
                        <a id={id} className={linkClass}>
                            {content}
                        </a>
                    </Link>
                )}
            </>
        );
    }

    return (
        <div className="d-block hover-grow ec-border-radius slider">
            {content}
        </div>
    );
};

export default function Swiper(props: Props): ReactElement | null {
    if (!props.slides || props.slides.length < 1) {
        return null;
    }

    const finalSectionTitle = props.sectionTitle
        ? props.sectionTitle.replace(/ /g, "-")
        : "";
    const idPrefix = `cc-egroups${
        props.pageName ? `-${props.pageName.toLowerCase()}` : ""
    }${props.selectedCampus ? `-${props.selectedCampus}` : ""}${
        finalSectionTitle ? `-find-${finalSectionTitle.toLowerCase()}` : ""
    }`;

    return (
        <div className="rel-pos swiper">
            <div
                slot="container-start"
                className={`swiper-prev-${props.uuid} hover-grow cursor-pointer slider-swiper-button-prev hide-xs-only swiper-button-left ec-cursor-pointer full-radius flex flex-justify-center flex-align-center w-40 ht-40 b-width-xs border-light-grey ec-background-white abs-pos pull-forward`}
                id={`${idPrefix}-arrow-prev`}
            >
                <FontAwesomeIcon icon={faAngleLeft} />
            </div>
            <SwiperCoreComponent
                spaceBetween={0}
                loop={
                    process.browser && window.innerWidth < props.desktopMinSize
                        ? false
                        : props.loop
                }
                centeredSlides={props.center}
                slidesPerView={props.slidesPerView}
                preventClicks={true}
                navigation={{
                    nextEl: `.swiper-next-${props.uuid}`,
                    prevEl: `.swiper-prev-${props.uuid}`,
                }}
                threshold={10}
                style={{ padding: "10px 0px" }}
            >
                {props.slides.map((slide, index) => {
                    return (
                        <SwiperSlide key={index} className="p-spacer-xs">
                            <SwiperItem
                                selectedCampus={props.selectedCampus}
                                slide={slide}
                                desktopMinSize={props.desktopMinSize}
                                slideHeight={props.slideHeight}
                                slideWidth={props.slideWidth}
                                sectionTitle={props.sectionTitle}
                                pageName={props.pageName}
                            />
                        </SwiperSlide>
                    );
                })}
            </SwiperCoreComponent>
            <div
                slot="container-end"
                className={`swiper-next-${props.uuid} hover-grow cursor-pointer slider-swiper-button-next swiper-button-right hide-xs-only ec-cursor-pointer full-radius flex flex-justify-center flex-align-center w-40 ht-40 b-width-xs border-light-grey ec-background-white abs-pos pull-forward`}
                id={`${idPrefix}-arrow-next`}
            >
                <FontAwesomeIcon icon={faAngleRight} />
            </div>
        </div>
    );
}
